import { ApolloError } from '@apollo/client';
import { useCascade, useMutation, useTranslation } from '@gimlite/common';
import { WizardItemType, Zone } from '@gimlite/ui-v1';
import { useEffect, useState } from 'react';
import {
  Mutation,
  MutationCreateBreakdownArgs,
} from '../../../../client/graphql';
import { createBreakdownGQL } from '../../../../common/gql/createBreakdown.gql';
import { ContractMotoristLine } from '../../../../composition/line/contract-motorist.line';
import { ReportTechnicalWizardInfos } from '../../../../composition/wizard-infos/report-technical.wizard-infos';
import {
  SummaryForm,
  SummaryFormType,
} from '../../../../form/summary/summary.form';
import { ReportTechnicalDeviceFailureContext } from '../../reportTechnicalDeviceFailure.wizard';

type ResultPosibility = any;
//  Mutation['createBreakdown']

export const SummaryStep = ({
  context: { issue, parkingId, contractsConcerned: motoristsConcerned },
  validate,
  submit,
}: WizardItemType.Config.Component<ReportTechnicalDeviceFailureContext>) => {
  const { t, lang } = useTranslation();

  const [created, setCreated] = useState<SummaryFormType.Data.Line[]>();

  const [createBreakdownCall] = useMutation<
    { createBreakdown: Mutation['createBreakdown'] },
    MutationCreateBreakdownArgs
  >(createBreakdownGQL);

  const { isFinish } = useCascade<ResultPosibility>([
    async () => {
      try {
        const response = await createBreakdownCall({
          variables: {
            input: {
              parkingId: parkingId!,
              category: issue.category,
              endDate: issue.endDate || '',
              description: issue.description || '',
              type: issue.accessPointType,
            },
          },
        });

        if (
          response?.errors &&
          Array.isArray(response?.errors) &&
          response?.errors.length > 0
        ) {
          throw response?.errors[0]?.message;
        }

        setCreated(() =>
          motoristsConcerned.map(
            ({ _id, name1, name2, ospContractId, isEnabled }) => ({
              _id,
              items: {
                component: (
                  <ContractMotoristLine
                    data={{
                      firstName: name1 || null,
                      lastName: name2 || null,
                      contractId: ospContractId || null,
                      status: isEnabled ? 'ACTIVE' : 'SUSPENDED',
                    }}
                    config={{ action: false }}
                  />
                ),
                state: { type: 'success' },
              },
            }),
          ),
        );

        return {
          title: 'ReportTechnicalDeviceFailure',
          response: response?.data,
        };
      } catch (error: unknown) {
        setCreated(() =>
          motoristsConcerned.map(
            ({ _id, name1, name2, ospContractId, isEnabled }) => ({
              _id,
              items: {
                component: (
                  <ContractMotoristLine
                    data={{
                      firstName: name1 || null,
                      lastName: name2 || null,
                      contractId: ospContractId || null,
                      status: isEnabled ? 'ACTIVE' : 'SUSPENDED',
                    }}
                    config={{ action: false }}
                  />
                ),
                state: {
                  type: 'error',
                  message:
                    error instanceof ApolloError
                      ? `${error.message}`
                      : `${error}`,
                },
              },
            }),
          ),
        );

        return {
          title: 'ReportTechnicalDeviceFailure',
          response: new Error(
            error instanceof ApolloError ? `${error.message}` : `${error}`,
          ),
        };
      }
    },
  ]);

  useEffect(() => {
    validate(isFinish);
  }, [isFinish]);

  return (
    <Zone
      config={{
        gap: {
          y: 1,
          x: 1,
        },
        zones: [['info', 'list']],
        rows: ['1fr'],
        columns: ['min-content', '1fr'],
      }}
    >
      <Zone.Area config={{ area: 'info' }}>
        <ReportTechnicalWizardInfos
          data={{
            startDate: issue.startDate,
            endDate: issue.endDate,
            accessPointType: issue.accessPointType,
            category: issue.category,
            description: issue.description,
          }}
        />
      </Zone.Area>

      <Zone.Area config={{ area: 'list' }}>
        <SummaryForm
          data={{
            list: created,
          }}
        />
      </Zone.Area>
    </Zone>
  );
};
